import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import { useQueryClient, InfiniteData } from '@tanstack/react-query';
import {
  BreadcrumbHeading,
  Modal,
  SnackbarStatus,
  Spinner,
  Typography,
  useSnackbar,
} from '@weareopenr/component-library';
import { getBrandId } from 'src/helpers/getBrandId/getBrandId';
import { Routes } from 'src/routes';
import { useUserTracking } from 'src/hooks/useUserTracking/useUserTracking';
import { useSchedules } from 'src/hooks/useSchedules/useSchedules';
import { QueryKeys } from 'src/hooks/QueryKeys';
import { usePublishScheduleFilters } from 'src/hooks/usePublishScheduleFilters';
import { ScheduleStatus, ValidationStatus, ISchedule } from 'src/api/getSchedules/types';
import type { IGetSchedulesResponse } from 'src/api/getSchedules/types';
import { IDisplaySchedule, mapSchedules } from 'src/utils/schedules/mapSchedules';
import { SummaryView } from 'src/components/ProductPublishModal/steps/SummaryView/SummaryView';
import { PublishType } from 'src/components/ProductPublishModal/types';
import { EVENT_TYPES } from 'src/api/getEvents/types';
import chefPlating from 'src/assets/images/chef-plating.png';
import { assignScheduleStatus } from 'src/utils/schedules/assignScheduleStatus';
import { groupPublishingByWeek } from 'src/utils/schedules/groupPublishingByWeek';
import { getPublishCardDetails } from 'src/utils/schedules/getPublishCardDetails';
import { getValidationRules } from 'src/utils/schedules/getValidationRules';
import { ScheduleCard } from 'src/components/ScheduleCard';
import { ValidationReportContainer } from 'src/components/ValidationReportContainer';
import CollapsibleSection from 'src/components/CollapsibleSection';
import SkeletonContainer from 'src/components/SkeletonContainer';
import { useValidate } from 'src/hooks/useValidate/useValidate';
import { useDeleteSchedules } from 'src/hooks/useDeleteSchedules/useDeleteSchedules';
import { DeleteSchedulePrompt } from 'src/components/DeleteSchedulePrompt';
import dayjs from 'dayjs';
import { PublishScheduleFilters } from './PublishScheduleFilters';
import styles from './PublishSchedule.module.scss';

export const PublishSchedule = () => {
  const { trackEvent } = useUserTracking();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { state, search } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const reportScheduleId = searchParams.get('report');
  const queryClient = useQueryClient();

  const [newScheduleId] = useState<string | null>(state?.newScheduleId ?? null);
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useSchedules(ScheduleStatus.SCHEDULED);
  const newScheduleRef = useRef<HTMLDivElement | null>(null);
  const hasScrolledToNew = useRef(false);
  const [openReportId, setOpenReportId] = useState<string | null>(null);
  const handledReportScheduleIdRef = useRef<string | null>(null);
  const [schedule, setSchedule] = useState<IDisplaySchedule | null>(null);
  const validationStatusesRef = useRef<Map<string, ValidationStatus>>(new Map());
  const [scheduleToDelete, setScheduleToDelete] = useState<ISchedule | null>(null);
  const { mutate: deleteSchedule, isLoading: isDeletingSchedule } = useDeleteSchedules();
  const { filterSchedules, filterWeeks, ...filterProps } = usePublishScheduleFilters();
  const validate = useValidate();

  const { ref } = useInView({
    onChange: (inView) => {
      // when redirected with newScheduleId, let the effect drive fetching until the target schedule is found and scrolled to;
      // only then hand control back to the intersection observer for regular infinite scroll
      if (inView && hasNextPage && (!newScheduleId || hasScrolledToNew.current)) fetchNextPage();
    },
  });

  useEffect(() => {
    document.title = 'Upcoming Publishes';
    trackEvent('page_viewed', { page: 'publish_schedule' });
  }, [trackEvent]);

  const handleRunValidation = async (schedule: ReturnType<typeof mapSchedules>[number]) => {
    const prevStatus = validationStatusesRef.current.get(schedule.schedule_id);

    queryClient.setQueryData<InfiniteData<IGetSchedulesResponse>>(
      [getBrandId(), QueryKeys.schedules, ScheduleStatus.SCHEDULED],
      (old) => assignScheduleStatus(old, schedule.schedule_id, ValidationStatus.VALIDATING),
    );
    showSnackbar({ status: SnackbarStatus.PROGRESS, message: 'Your schedule validation is in progress' });

    try {
      await validate.mutateAsync({
        criteria: {
          schedule_id: schedule.schedule_id,
          ...(schedule.price_scheme ? { price_scheme_id: schedule.price_scheme.price_scheme_id } : {}),
          rules: getValidationRules(schedule.event_type),
        },
      });
    } catch {
      if (prevStatus === undefined) {
        validationStatusesRef.current.delete(schedule.schedule_id);
      } else {
        queryClient.setQueryData<InfiniteData<IGetSchedulesResponse>>(
          [getBrandId(), QueryKeys.schedules, ScheduleStatus.SCHEDULED],
          (old) => assignScheduleStatus(old, schedule.schedule_id, prevStatus),
        );
        validationStatusesRef.current.set(schedule.schedule_id, prevStatus);
      }
      showSnackbar({ status: SnackbarStatus.ERROR, message: 'Failed to trigger validation. Please try again.' });
    } finally {
      queryClient.invalidateQueries({ queryKey: [getBrandId(), QueryKeys.schedules] });
    }
  };

  // Clear newScheduleId from history state on mount so a page refresh doesn't re-trigger the scroll.
  useEffect(() => {
    if (newScheduleId) {
      globalThis.history.replaceState({ ...state, newScheduleId: undefined }, '');
    }
  }, []);

  const schedules = useMemo(
    () => filterSchedules(mapSchedules(data?.pages.flatMap((page) => page.data) ?? [])),
    [data, filterSchedules],
  );
  const allWeeks = useMemo(() => groupPublishingByWeek(schedules), [schedules]);
  const weeks = useMemo(() => filterWeeks(allWeeks), [allWeeks, filterWeeks]);

  // Deep-link from a validation notification (?report=<schedule_id>) - the
  // report itself is keyed by report_id, so wait for the matching schedule to
  // load before opening it. Tracked via a ref (not openReport state) so
  // dismissing the modal doesn't cause this to immediately reopen it - it
  // should only ever auto-open once per distinct reportScheduleId.
  useEffect(() => {
    if (!reportScheduleId || handledReportScheduleIdRef.current === reportScheduleId) return;
    const match = schedules.find((s) => s.schedule_id === reportScheduleId);
    if (match?.validation.report_id) {
      handledReportScheduleIdRef.current = reportScheduleId;
      setOpenReportId(match.validation.report_id);
    }
  }, [reportScheduleId, schedules]);

  const closeReportModal = () => {
    setOpenReportId(null);

    if (reportScheduleId) {
      const params = new URLSearchParams(searchParams);
      params.delete('report');
      navigate({ pathname: Routes.PUBLISH_SCHEDULE, search: params.toString() }, { replace: true });
    }
  };

  useEffect(() => {
    schedules.forEach((schedule) => {
      const prev = validationStatusesRef.current.get(schedule.schedule_id);
      const current = schedule.validation.status;
      validationStatusesRef.current.set(schedule.schedule_id, current);

      if (prev !== ValidationStatus.VALIDATING) return;
      if (current === ValidationStatus.VALIDATED)
        showSnackbar({ status: SnackbarStatus.SUCCESS, message: `"${schedule.schedule_name}" validated successfully` });
      if (current === ValidationStatus.VALIDATION_FAILED)
        showSnackbar({
          status: SnackbarStatus.ERROR,
          message: `Validation for "${schedule.schedule_name}" surfaced issues`,
        });
    });
  }, [schedules]);

  const newScheduleLoaded = !!newScheduleId && schedules.some((s) => s.schedule_id === newScheduleId);
  useEffect(() => {
    if (!newScheduleId || hasScrolledToNew.current) return;
    if (newScheduleLoaded) {
      hasScrolledToNew.current = true;
      newScheduleRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [newScheduleId, newScheduleLoaded, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleDeleteConfirm = () => {
    if (!scheduleToDelete) return;
    deleteSchedule([scheduleToDelete.schedule_id], {
      onSuccess: () => {
        showSnackbar({ status: SnackbarStatus.SUCCESS, message: 'Schedule deleted successfully' });
        setScheduleToDelete(null);
      },
      onError: () => {
        showSnackbar({ status: SnackbarStatus.ERROR, message: 'Failed to delete schedule' });
      },
    });
  };

  return (
    <>
      <div className={styles['page-container']}>
        <div className={styles['page-header']}>
          <BreadcrumbHeading breadcrumbs={[{ label: 'Upcoming Publishes', path: Routes.PUBLISH_SCHEDULE }]} />
          <div className={styles['filters']}>
            <PublishScheduleFilters {...filterProps} weeks={allWeeks} />
          </div>
        </div>
        <div className={styles['schedules']}>
          {isLoading && <SkeletonContainer hiddenTitle />}
          {weeks.map(({ key, title, items }) => (
            <CollapsibleSection
              key={key}
              title={title}
              size="medium"
              defaultExpanded
              bordered
              arrowPosition="left"
              arrowColour="liquorice"
              scrollable={false}
            >
              <div className={styles['schedule-group']}>
                {items.map((schedule) => {
                  const isMenuPublish = schedule.event_type === EVENT_TYPES.PUBLISH_MENU;

                  return (
                    <div
                      key={schedule.schedule_id}
                      ref={schedule.schedule_id === newScheduleId ? newScheduleRef : undefined}
                    >
                      <ScheduleCard
                        type={schedule.event_type}
                        name={schedule.schedule_name}
                        scheduledTime={schedule.scheduled_for}
                        // CAUTION (post-sync staleness) is menu-only, and takes
                        // priority over the pre-publish validation status.
                        status={
                          isMenuPublish && schedule.status === ScheduleStatus.CAUTION
                            ? schedule.status
                            : schedule.validation.status
                        }
                        details={getPublishCardDetails(schedule, schedule.schedule_name)}
                        validationTime={schedule.validation.validated_at}
                        dataSnapshotDate={schedule.create_date}
                        isReportAvailable={
                          isMenuPublish
                            ? schedule.validation.status === ValidationStatus.VALIDATION_FAILED &&
                              !!schedule.validation.report_id
                            : !!schedule.validation.report_id
                        }
                        actions={{
                          onViewScheduleDetails: () => setSchedule(schedule),
                          // Menu validation runs automatically on schedule creation and can't be
                          // manually re-triggered from here - see ScheduleCard's onRunValidation doc.
                          ...(isMenuPublish ? {} : { onRunValidation: () => handleRunValidation(schedule) }),
                          onViewReport: () =>
                            schedule.validation.report_id && setOpenReportId(schedule.validation.report_id),
                          onDelete: () => setScheduleToDelete(schedule),
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            </CollapsibleSection>
          ))}
          {!isLoading && weeks.length === 0 && (
            <div className={styles['no-schedules']}>
              <Typography>No upcoming schedules...</Typography>
            </div>
          )}
          {isFetchingNextPage && (
            <div className={styles['spinner-container']}>
              <Spinner size="medium" colour="rainbow" />
            </div>
          )}
          {hasNextPage && !isFetchingNextPage && <div ref={ref} />}
        </div>
      </div>
      {openReportId && <ValidationReportContainer isOpen reportId={openReportId} closeModal={closeReportModal} />}
      {schedule && (
        <Modal
          open
          image={chefPlating}
          imageAlt="Picture of chef at the plating station"
          closeModal={() => setSchedule(null)}
        >
          <SummaryView
            defaultValues={{
              type: schedule?.event_type === EVENT_TYPES.PUBLISH_PRICING ? PublishType.PRICING : PublishType.PRODUCT,
              priceSchemeId: schedule?.price_scheme?.price_scheme_id ?? null,
              instanceId: null,
              venues: schedule?.venues.map((v) => v.venue_id) ?? [],
              scheduleName: schedule?.schedule_name,
              publicationDate: schedule ? new Date(schedule.scheduled_for) : null,
              excludedItems: schedule?.excluded_items,
            }}
            publishTypeLabel={
              schedule?.event_type === EVENT_TYPES.PUBLISH_MENU
                ? { title: 'Menu Publish', description: 'Menu data' }
                : undefined
            }
            onPublish={() => setSchedule(null)}
          />
        </Modal>
      )}
      <DeleteSchedulePrompt
        open={!!scheduleToDelete}
        name={scheduleToDelete?.schedule_name || ''}
        scheduledTime={scheduleToDelete ? dayjs(scheduleToDelete.scheduled_for).format('ddd D MMM - HH:mm') : ''}
        onClose={() => setScheduleToDelete(null)}
        onConfirm={handleDeleteConfirm}
        isSubmitting={isDeletingSchedule}
      />
    </>
  );
};
